/* eslint-disable react/require-default-props */
import {
  forwardRef, ReactNode, RefObject, useContext, useEffect, useState,
} from 'react';
import styles from './IframeContentOverlay.module.css';
import { CanvasContext } from '../Canvas/context';

export const IFRAME_CONTENT_OVERLAY_ID = 'iframe-content-overlay-container';

interface IframeContentOverlayProps {
  iframeRef: RefObject<HTMLIFrameElement>
  children?: ReactNode
}

export const IframeContentOverlay = forwardRef<
HTMLDivElement,
IframeContentOverlayProps
>((props, ref) => {
  const iframeDoc = props.iframeRef.current?.contentDocument;
  const { scale } = useContext(CanvasContext);

  const [scrollY, setScrollY] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!iframeDoc) return undefined;

    let ticking = false;
    const handleScroll = (e: Event) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY((e.target as Document).defaultView?.scrollY ?? 0);
          ticking = false;
        });

        ticking = true;
      }
    };

    iframeDoc?.addEventListener('scroll', handleScroll);

    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          // Firefox implements `contentBoxSize` as a single content rect, rather than an array
          const contentBoxSize = Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize;

          setHeight(contentBoxSize.blockSize);
        }
      }
    });

    if (iframeDoc?.body) {
      resizeObserver.observe(iframeDoc?.body);
    }

    return () => {
      iframeDoc?.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [iframeDoc]);

  return (
    <div className={styles.overlayContainer} id={IFRAME_CONTENT_OVERLAY_ID}>
      <div
        ref={ref}
        className={styles.overlay}
        style={{
          height: height ? `${height / (scale || 1)}px` : undefined,
          transform: `translateY(-${scrollY / (scale || 1)}px)`,
        }}
      >
        {props.children}
      </div>
    </div>
  );
});
